import classNames from 'classnames';
import { FC, ReactNode } from 'react';

import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { IconAncap } from 'src/general/Icons/IconAncap';
import { IconCalendar } from 'src/general/Icons/IconCalendar';
import { IconCalendarWithTick } from 'src/general/Icons/IconCalendarWithTick';
import { IconColour } from 'src/general/Icons/IconColour';
import { IconDriveType } from 'src/general/Icons/IconDriveType';
import { IconEngine } from 'src/general/Icons/IconEngine';
import { IconEnginePower } from 'src/general/Icons/IconEnginePower';
import { IconFuelConsumption } from 'src/general/Icons/IconFuelConsumption';
import { IconNumberOfKeys } from 'src/general/Icons/IconNumberOfKeys';
import { IconOdometer } from 'src/general/Icons/IconOdometer';
import { IconPetrol } from 'src/general/Icons/IconPetrol';
import { IconSeats } from 'src/general/Icons/IconSeats';
import { IconSpanner } from 'src/general/Icons/IconSpanner';
import { IconTransmission } from 'src/general/Icons/IconTransmission';
import { IconVIN } from 'src/general/Icons/IconVIN';
import { getMicrocopy } from 'src/stores/microcopyStore';

import styles from './CarKeyInformation.module.scss';
import { KEY_ITEMS } from './CarKeyInformation.type';

interface Item {
  key: KEY_ITEMS;
  value: string;
}

interface Props {
  items: Item[];
}

type KeyItemProps = {
  [key in KEY_ITEMS]: {
    icon: ReactNode;
    microcopyKey: MICROCOPY;
  };
};

const keyItem: KeyItemProps = {
  [KEY_ITEMS.KILOMETERS]: {
    icon: <IconOdometer />,
    microcopyKey: MICROCOPY.PDP_KEY_INFORMATION_KILOMETERS,
  },
  [KEY_ITEMS.COMPLIANCE_YEAR]: {
    icon: <IconCalendarWithTick />,
    microcopyKey: MICROCOPY.PDP_KEY_INFORMATION_COMPLIANCE_YEAR,
  },
  [KEY_ITEMS.BUILD_YEAR]: {
    icon: <IconCalendar />,
    microcopyKey: MICROCOPY.PDP_KEY_INFORMATION_BUILD_YEAR,
  },
  [KEY_ITEMS.ENGINE_SIZE]: {
    icon: <IconEngine />,
    microcopyKey: MICROCOPY.PDP_KEY_INFORMATION_ENGINE,
  },
  [KEY_ITEMS.FUEL_TYPE]: {
    icon: <IconPetrol />,
    microcopyKey: MICROCOPY.PDP_KEY_INFORMATION_FUEL_TYPE,
  },
  [KEY_ITEMS.TRANSMISSION]: {
    icon: <IconTransmission />,
    microcopyKey: MICROCOPY.PDP_KEY_INFORMATION_TRANSMISSION,
  },
  [KEY_ITEMS.DRIVE_TYPE]: {
    icon: <IconDriveType />,
    microcopyKey: MICROCOPY.PDP_CAR_SUMMARY_ITEM_EXTERIOR_TITLE_3,
  },
  [KEY_ITEMS.SEATS]: {
    icon: <IconSeats />,
    microcopyKey: MICROCOPY.PDP_KEY_INFORMATION_SEATS,
  },
  [KEY_ITEMS.LAST_SERVICE_DATE]: {
    icon: <IconSpanner />,
    microcopyKey: MICROCOPY.PDP_KEY_INFORMATION_LAST_SERVICE_DATE,
  },
  [KEY_ITEMS.VIN_NUMBER]: {
    icon: <IconVIN />,
    microcopyKey: MICROCOPY.PDP_KEY_INFORMATION_VIN_NUMBER,
  },
  [KEY_ITEMS.FUEL_CONSUMPTION]: {
    icon: <IconFuelConsumption />,
    microcopyKey: MICROCOPY.PDP_CAR_SUMMARY_ITEM_FUEL_TITLE_1,
  },
  [KEY_ITEMS.ANCAP_SAFETY_RATING]: {
    icon: <IconAncap />,
    microcopyKey: MICROCOPY.PDP_CAR_SUMMARY_ITEM_FUEL_TITLE_3,
  },
  [KEY_ITEMS.KEYS]: {
    icon: <IconNumberOfKeys />,
    microcopyKey: MICROCOPY.PDP_CAR_SUMMARY_ITEM_INTERIOR_TITLE_3,
  },
  [KEY_ITEMS.EXTERIOR_COLOR]: {
    icon: <IconColour />,
    microcopyKey: MICROCOPY.PDP_CAR_SUMMARY_ITEM_EXTERIOR_TITLE_2,
  },
  [KEY_ITEMS.ENGINE_POWER]: {
    icon: <IconEnginePower />,
    microcopyKey: MICROCOPY.PDP_CAR_SUMMARY_ITEM_MECHANICAL_TITLE_3,
  },
};

export const CarKeyInformation: FC<Props> = ({ items }) => (
  <section className={styles.root}>
    <dl className={styles.layout}>
      {items.length !== 0 &&
        items.map(({ value, key }) => (
          <div
            className={classNames({
              [styles.infoWithVin]: items.find(({ key }) => key === KEY_ITEMS.VIN_NUMBER),
              [styles.info]: items.find(({ key }) => key !== KEY_ITEMS.VIN_NUMBER),
            })}
            key={key}
          >
            <div className={styles.infoIcon} aria-hidden="true">
              {keyItem[key].icon}
            </div>
            <dt className={styles.infoName}>{getMicrocopy(keyItem[key].microcopyKey)}</dt>
            <dd
              className={styles.infoValue}
              data-testid={`CarKeyInformation-component-${getMicrocopy(keyItem[key].microcopyKey)}-info`}
            >
              {value}
            </dd>
          </div>
        ))}
    </dl>
  </section>
);
